import { HostedFormId, IAnswer, Question } from "@sp-crm/core";
import { Header } from "components/header";
import { LayoutFormItem } from "components/layout/layout-form-section";
import { useQueryParams } from "components/shared/hooks";
import { FileUpload, UploadedFile } from "components/ui/file-upload";
import { InlineBanner } from "components/ui/inline-banner";
import { PrimaryButton } from "components/ui/primary-button";
import { Spinner } from "components/ui/spinner";
import { HostedForm, HostedFormStyle } from "generated/graphql";
import { produce } from "immer";
import React, { useCallback, useEffect, useMemo } from "react";
import { useQuery } from "react-query";
import { useRouteMatch } from "react-router";
import * as http from "util/http";

type UpdatePayload = Record<string, unknown> & { answers: IAnswer[] };

type HostedFormResponse = HostedForm & {
    initialData: Record<string, unknown>;
    logo?: string;
    sessionId?: string;
    slugConfiguration?: {
        id: string | null;
        slug: string | null;
        regionId: string | null;
        referenceBusinessId: string | null;
        referenceContactId: string | null;
        referenceCommunityId: string | null;
        hostedFormId: string | null;
    };
};

interface State {
    pageState: "loading" | "inProgress" | "submitted" | "error";
    response: HostedFormResponse | null;
    style: HostedFormStyle;
    payload: UpdatePayload;
    submitting: boolean;
    uploadedFiles: UploadedFile[];
    logo?: string;
    errorMessage?: string;
    submitErrorMessage?: string;
    hostedFormId?: string;
    regionId?: string;
    referenceBusinessId?: string;
    referenceContactId?: string;
    referenceCommunityId?: string;
    sessionId?: string;
}

const initialState: State = {
    pageState: "loading",
    response: null,
    payload: { answers: [] },
    submitting: false,
    uploadedFiles: [],
    style: HostedFormStyle.Embedded,
};

type Action =
    | { type: "loadComplete"; response: HostedFormResponse }
    | { type: "loadError"; message: string }
    | { type: "upsertAnswer"; answer: IAnswer }
    | { type: "setField"; value: unknown; fieldName: string }
    | { type: "updateFiles"; files: UploadedFile[] }
    | { type: "startSubmit" }
    | { type: "submitComplete" }
    | { type: "submitError"; message?: string }
    | {
          type: "initialLoad";

          hostedFormId: string;
          regionId: string;
          referenceBusinessId: string;
          referenceContactId: string;
          referenceCommunityId: string;
      };

const reducer = (state: State, action: Action): State =>
    produce(state, draft => {
        if (action.type === "loadComplete") {
            if (draft.pageState === "loading") {
                draft.pageState = "inProgress";
                draft.response = action.response;
                if (action.response.configuration.style) {
                    draft.style = action.response.configuration.style;
                }
                if (action.response.logo) {
                    draft.logo = action.response.logo;
                }
                if (action.response.sessionId) {
                    draft.sessionId = action.response.sessionId;
                }
                draft.payload = action.response.initialData as UpdatePayload;
                if (action.response.slugConfiguration) {
                    const config = action.response.slugConfiguration;
                    if (config.hostedFormId) draft.hostedFormId = config.hostedFormId;
                    if (config.regionId) draft.regionId = config.regionId;
                    if (config.referenceBusinessId)
                        draft.referenceBusinessId = config.referenceBusinessId;
                    if (config.referenceContactId)
                        draft.referenceContactId = config.referenceContactId;
                    if (config.referenceCommunityId)
                        draft.referenceCommunityId = config.referenceCommunityId;
                }
            }
        }
        if (action.type === "loadError") {
            draft.pageState = "error";
            draft.errorMessage = action.message;
        }
        if (action.type === "upsertAnswer") {
            const { answer } = action;

            const existingAnswerIndex = draft.payload.answers.findIndex(
                a => a.questionId === answer.questionId,
            );

            if (existingAnswerIndex === -1) {
                draft.payload.answers.push(answer);
            } else {
                draft.payload.answers[existingAnswerIndex] = answer;
            }
        }
        if (action.type === "setField") {
            const { fieldName, value } = action;

            draft.payload[fieldName] = value;
        }
        if (action.type === "updateFiles") {
            draft.uploadedFiles = action.files;
        }
        if (action.type === "startSubmit") {
            draft.submitting = true;
            draft.submitErrorMessage = undefined;
        }
        if (action.type === "submitComplete") {
            draft.submitting = false;
            draft.pageState = "submitted";
        }
        if (action.type === "submitError") {
            draft.submitting = false;
            draft.submitErrorMessage =
                action.message ||
                "An error occurred while submitting the form. Please try again.";
        }
        if (action.type === "initialLoad") {
            draft.hostedFormId = action.hostedFormId;
            draft.regionId = action.regionId;
            draft.referenceBusinessId = action.referenceBusinessId;
            draft.referenceContactId = action.referenceContactId;
            draft.referenceCommunityId = action.referenceCommunityId;
        }
    });

function dateAnswerToString(answer: IAnswer): string | null {
    if (!answer?.dateAnswer) {
        return null;
    }

    if (typeof answer.dateAnswer === "string") {
        return answer.dateAnswer;
    }

    return answer.dateAnswer.toISOString();
}

export const HostedFormExperience: React.FC<unknown> = props => {
    const [state, dispatch] = React.useReducer(reducer, initialState);
    const { hostedFormId } = useRouteMatch().params as {
        hostedFormId: HostedFormId;
    };
    const queryParams = useQueryParams();

    const regionId = queryParams["regionId"] || null;
    const referenceBusinessId = queryParams["referenceBusinessId"] || null;
    const referenceContactId = queryParams["referenceContactId"] || null;
    const referenceCommunityId = queryParams["referenceCommunityId"] || null;
    useEffect(() => {
        dispatch({
            type: "initialLoad",
            hostedFormId,
            regionId,
            referenceBusinessId,
            referenceContactId,
            referenceCommunityId,
        });
    }, [
        hostedFormId,
        regionId,
        referenceBusinessId,
        referenceContactId,
        referenceCommunityId,
        dispatch,
    ]);

    const requestDescription = useMemo(
        () => http.newRequestDescription("/api/hosted-form/:id"),
        [],
    );

    const fetchHostedForm = useCallback(async () => {
        const url = `/api/hosted-form/${hostedFormId}`;

        const response = await http.get(requestDescription, url);
        return response.data as HostedFormResponse;
    }, [hostedFormId, requestDescription]);

    useQuery("getHostedForm", fetchHostedForm, {
        onSuccess: data => dispatch({ type: "loadComplete", response: data }),
        onError: (err: { response?: { data?: { error?: string } } }) => {
            if (typeof err?.response?.data?.error === "string") {
                dispatch({ type: "loadError", message: err.response.data.error });
            } else {
                dispatch({
                    type: "loadError",
                    message:
                        "An unexpected error occurred. Contact the person who sent you this link for help.",
                });
            }
        },
    });

    const handleSave = async () => {
        dispatch({ type: "startSubmit" });

        const fields = {
            ...state.payload,
        };
        delete fields.answers;
        const answers = state.payload.answers.map(answer => ({
            questionId: answer.questionId,
            booleanAnswer: answer.booleanAnswer,
            numberAnswer: answer.numberAnswer,
            numberAnswerHigh: answer.numberAnswerHigh,
            selectionAnnotations: answer.selectionAnnotations,
            selections: answer.selections,
            dateAnswer: dateAnswerToString(answer),
            textAnswer: answer.textAnswer,
            idReferenceAnswer: answer.idReferenceAnswer,
            calendarDateAnswer: answer.calendarDateAnswer as unknown as string,
        }));

        try {
            const response = await http.post(
                http.newRequestDescription("/api/hosted-form/:id/response"),
                `/api/hosted-form/${state.hostedFormId}/response`,
                {
                    sessionId: state.sessionId,
                    regionId: state.regionId,
                    referenceBusinessId: state.referenceBusinessId,
                    referenceContactId: state.referenceContactId,
                    referenceCommunityId: state.referenceCommunityId,
                    fields,
                    answers,
                },
            );
            const isSuccessful = response.status === 200 || response.status === 201;
            if (isSuccessful) {
                dispatch({ type: "submitComplete" });
            } else {
                dispatch({
                    type: "submitError",
                    message: "Failed to submit form. Please try again.",
                });
            }
        } catch (error: unknown) {
            let errorMessage =
                "An unexpected error occurred while submitting the form. Please try again.";

            if (error && typeof error === "object" && "response" in error) {
                const response = (error as { response?: { data?: { msg?: string } } })
                    .response;
                if (
                    response &&
                    typeof response.data === "object" &&
                    response.data &&
                    "msg" in response.data
                ) {
                    errorMessage = response.data.msg || errorMessage;
                }
            } else if (error instanceof Error) {
                errorMessage = error.message;
            }

            dispatch({ type: "submitError", message: errorMessage });
        }
    };

    if (state.style === HostedFormStyle.Embedded) {
        return (
            <div className="bg-white min-h-full p-4">
                {state.pageState === "loading" ? <Spinner /> : null}
                {state.pageState === "error" ? (
                    <InlineBanner type="error">{state.errorMessage}</InlineBanner>
                ) : null}
                {state.pageState === "submitted" ? (
                    <InlineBanner type="success">
                        Thank you for your submission. We will review the information and
                        follow up with next steps.
                    </InlineBanner>
                ) : null}
                {state.pageState === "inProgress" ? (
                    <>
                        <HostedFormComponent
                            form={state.response}
                            submitting={state.submitting}
                            values={state.payload}
                            uploadedFiles={state.uploadedFiles}
                            sessionId={state.sessionId}
                            onCommitAnswer={answer => {
                                dispatch({ type: "upsertAnswer", answer });
                            }}
                            onCommitField={(fieldName, value) => {
                                dispatch({ type: "setField", fieldName, value });
                            }}
                            onFilesChange={files => {
                                dispatch({ type: "updateFiles", files });
                            }}
                            onSave={handleSave}
                            style={state.style}
                        />
                        {state.submitErrorMessage && (
                            <InlineBanner type="error">
                                {state.submitErrorMessage}
                            </InlineBanner>
                        )}
                    </>
                ) : null}
            </div>
        );
    }
    if (state.style === HostedFormStyle.Standalone) {
        const logo = state.logo;
        return (
            <div className="min-h-screen flex flex-col bg-gray-100">
                <header className="flex-none bg-white shadow">
                    <div className="px-2 py-2 space-y-2 items-center">
                        {logo ? (
                            <div className="flex justify-center">
                                <img
                                    className="max-w-[600px] max-h-[200px] object-contain"
                                    src={`data:image/png;base64,${logo}`}
                                />
                            </div>
                        ) : null}
                    </div>
                </header>
                <div className="my-4 lg:my-8">
                    {state.pageState === "loading" ? <Spinner /> : null}
                    {state.pageState === "error" ? (
                        <InlineBanner type="error">{state.errorMessage}</InlineBanner>
                    ) : null}
                    {state.pageState === "submitted" ? (
                        <div className="max-w-screen-lg rounded p-4 shadow bg-white mx-auto">
                            <InlineBanner type="success">
                                Thank you for your submission. We will review the
                                information and follow up with next steps.
                            </InlineBanner>
                        </div>
                    ) : null}
                    {state.pageState === "inProgress" ? (
                        <>
                            <HostedFormComponent
                                form={state.response}
                                submitting={state.submitting}
                                values={state.payload}
                                uploadedFiles={state.uploadedFiles}
                                sessionId={state.sessionId}
                                onCommitAnswer={answer => {
                                    dispatch({ type: "upsertAnswer", answer });
                                }}
                                onCommitField={(fieldName, value) => {
                                    dispatch({ type: "setField", fieldName, value });
                                }}
                                onFilesChange={files => {
                                    dispatch({ type: "updateFiles", files });
                                }}
                                onSave={handleSave}
                                style={state.style}
                            />
                            {state.submitErrorMessage && (
                                <div className="max-w-screen-lg mx-auto">
                                    <InlineBanner type="error">
                                        {state.submitErrorMessage}
                                    </InlineBanner>
                                </div>
                            )}
                        </>
                    ) : null}
                </div>
            </div>
        );
    }
    const _exhaustiveCheck: never = state.style;
    throw new Error(`Unhandled style: ${_exhaustiveCheck}`);
};

interface HostedFormComponentProps {
    form: HostedForm;
    values: Record<string, unknown>;
    uploadedFiles: UploadedFile[];
    sessionId?: string;
    onCommitAnswer: (answer: IAnswer) => void;
    onCommitField: (fieldName: string, value: unknown) => void;
    onFilesChange: (files: UploadedFile[]) => void;
    onSave: () => void;
    submitting: boolean;
    style: HostedFormStyle;
}

const formComponentStyles = {
    [HostedFormStyle.Embedded]: {
        container: "space-y-4 lg:space-y-8 max-w-xl",
        section: "",
        submit: "flex items-center space-x-2",
    },
    [HostedFormStyle.Standalone]: {
        container:
            "space-y-4 lg:space-y-0 lg:grid lg:grid-cols-2 lg:gap-4 md:max-w-xl lg:max-w-screen-lg mx-auto",
        section: "bg-white shadow-sm md:shadow md:rounded-lg p-4",
        submit: "bg-white p-4 flex items-center space-x-2 shadow-sm md:shadow md:rounded-lg lg:col-span-2",
    },
};

const HostedFormComponent: React.FC<HostedFormComponentProps> = props => {
    const {
        form,
        values,
        uploadedFiles,
        sessionId,
        onCommitAnswer,
        onCommitField,
        onFilesChange,
        onSave,
        submitting,
        style,
    } = props;

    const handleSubmit = useCallback(
        (e: React.FormEvent<HTMLFormElement>) => {
            e.preventDefault();
            onSave();
        },
        [onSave],
    );

    const formStyles = formComponentStyles[style];
    const fileUploadEnabled = form.configuration.fileUploadEnabled ?? false;
    const hasUploadingFiles = uploadedFiles.some(f => f.status === "uploading");
    const canSubmit = !submitting && !hasUploadingFiles;

    return (
        <form className={formStyles.container} onSubmit={handleSubmit}>
            {form.layoutSections
                .filter(s => s.visible)
                .map(section => {
                    return (
                        <div className={formStyles.section} key={section.id}>
                            <Header iconName={section.icon}>{section.title}</Header>
                            <div className="space-y-4">
                                {section.layoutItems.map(layoutItem => (
                                    <LayoutFormItem
                                        key={layoutItem.id}
                                        layoutItem={layoutItem}
                                        entity={values}
                                        fields={form.fields}
                                        questions={(form.questions || []).map(q =>
                                            Question.load(q),
                                        )}
                                        onCommitAnswer={onCommitAnswer}
                                        onCommitField={onCommitField}
                                    />
                                ))}
                            </div>
                        </div>
                    );
                })}

            {fileUploadEnabled && sessionId && (
                <div className={formStyles.section}>
                    <Header>Attachments</Header>
                    <div className="mt-4">
                        <FileUpload
                            uploadEndpoint={`/api/hosted-form/files/${sessionId}`}
                            maxFiles={10}
                            maxSizePerFile={5 * 1024 * 1024} // 5MB
                            maxTotalSize={50 * 1024 * 1024} // 50MB
                            onFilesChange={onFilesChange}
                            disabled={submitting}
                        />
                    </div>
                </div>
            )}

            <div className={formStyles.submit}>
                <PrimaryButton disabled={!canSubmit} type="submit">
                    {hasUploadingFiles ? "Uploading files..." : "Submit"}
                </PrimaryButton>
                {submitting || hasUploadingFiles ? <Spinner /> : null}
                {hasUploadingFiles && (
                    <span className="text-sm text-gray-600">
                        Please wait for all files to finish uploading
                    </span>
                )}
            </div>
        </form>
    );
};
